/**
 * useMortgageApi — React hook for the mortgage API service.
 *
 * Usage:
 *   const { loading, error, data, checkEligibility } = useMortgageApi();
 *   await checkEligibility({ annualIncome, monthlyExpenses, deposit, propertyPrice });
 */

import { useState, useCallback } from 'react';
import { mortgageService, mortgageAuthService, mortgageUtilityService, mortgageHelpers } from '../services/mortgage/mortgageApiService';

const useMortgageApi = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);

  const execute = useCallback(async (apiFn, ...args) => {
    setLoading(true);
    setError(null);
    try {
      const result = await apiFn(...args);
      setData(result);
      return result;
    } catch (err) {
      const normalizedError = {
        code: err?.code || 'UNKNOWN_ERROR',
        message: err?.message || 'An unexpected error occurred.',
        fields: err?.fields || null,
      };
      setError(normalizedError);
      throw normalizedError;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    // State
    loading,
    error,
    data,
    clearError: () => setError(null),
    clearData: () => setData(null),

    // Auth
    login: (credentials) => execute(mortgageAuthService?.login, credentials),
    logout: () => execute(mortgageAuthService?.logout),
    getProfile: () => execute(mortgageAuthService?.getProfile),
    isAuthenticated: mortgageAuthService?.isAuthenticated,

    // Mortgage calculations
    checkEligibility: (params) => execute(mortgageService?.checkEligibility, params),
    calculatePayment: (params) => execute(mortgageService?.calculatePayment, params),
    applyForApproval: (params) => execute(mortgageService?.applyForApproval, params),
    analyseRefinance: (params) => execute(mortgageService?.analyseRefinance, params),
    getRates: (params) => execute(mortgageService?.getRates, params),
    checkAffordability: (params) => execute(mortgageService?.checkAffordability, params),

    // Utility
    healthCheck: () => execute(mortgageUtilityService?.healthCheck),
    getLenders: () => execute(mortgageUtilityService?.getLenders),
    getInterestRates: () => execute(mortgageUtilityService?.getInterestRates),

    // Helpers (no async state needed)
    helpers: mortgageHelpers,
  };
};

export default useMortgageApi;
